import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { motion } from "framer-motion";

/* Shared pieces for the pattern-2 homepage (a copy of HomePattern with the
   team's palette roles and fewer arches). */

/** Hero arch mode for review: "one" keeps the hero arch as a signature, "none" swaps it out. */
export type ArchMode = "one" | "none";
export const ArchContext = createContext<ArchMode>("one");
export const useArchMode = () => useContext(ArchContext);
export const EASE = [0.22, 1, 0.36, 1] as const;
export const CLUB = "/club-preview/pattern";
export const HOME = "/home-preview/pattern-2";

/* ─── Torn paper ─── */

export function rng(seed: number) {
  return () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
}


export function tear(line: [number, number][], seed: number, amp = 5, step = 9) {
  const r = rng(seed), out: [number, number][] = [];
  for (let i = 0; i < line.length - 1; i++) {
    const [x1, y1] = line[i], [x2, y2] = line[i + 1];
    const len = Math.hypot(x2 - x1, y2 - y1), n = Math.max(1, Math.round(len / step));
    const nx = -(y2 - y1) / len, ny = (x2 - x1) / len;
    for (let k = 0; k < n; k++) {
      const t = k / n, j = (r() - 0.5) * 2 * amp * (r() < 0.15 ? 2 : 1);
      out.push([x1 + (x2 - x1) * t + nx * j, y1 + (y2 - y1) * t + ny * j]);
    }
  }
  out.push(line[line.length - 1]);
  return out;
}

/** A torn sheet: colored paper over a slightly larger white "fiber" edge. */
export function Torn({ edge, close, fill, seed, fiber = 6, jag = 1 }: { edge: [number, number][]; close: [number, number][]; fill: string; seed: number; fiber?: number; jag?: number }) {
  const pts = (shift: number, s: number) => {
    const e = tear(edge, s, (shift ? 6 : 4) * jag);
    // Push the fiber layer outward by sampling the edge's normal.
    const out = e.map(([x, y], i) => {
      const [ax, ay] = e[Math.max(0, i - 1)], [bx, by] = e[Math.min(e.length - 1, i + 1)];
      const len = Math.hypot(bx - ax, by - ay) || 1;
      return [x - ((by - ay) / len) * shift, y + ((bx - ax) / len) * shift] as [number, number];
    });
    return [...out, ...close].map(p => p.join(",")).join(" ");
  };
  return <>
    <polygon points={pts(fiber, seed + 7)} fill="var(--k-card)" style={{ filter: "drop-shadow(0 2px 3px #0000001f)" }} />
    <polygon points={pts(0, seed)} fill={fill} />
  </>;
}
/* ─── Bits ─── */

export function useFonts() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600..900;1,600..900&family=Nothing+You+Could+Do&family=Mrs+Saint+Delafield&family=Figtree:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
}

export function Draw({ d, delay = 0, width = 3, dash }: { d: string; delay?: number; width?: number; dash?: boolean }) {
  if (dash) return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeDasharray="9 9"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay, duration: 0.8 }} />;
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
    transition={{ pathLength: { duration: 0.55, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

export function Note({ children, className, style, delay }: { children: ReactNode; className: string; style?: CSSProperties; delay: number }) {
  return <motion.div className={`p2-abs ${className}`} style={style} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay, duration: 0.5 }}>{children}</motion.div>;
}

export const Heart = ({ delay = 0 }: { delay?: number }) => <svg className="p2-heart" viewBox="0 0 30 28" aria-hidden="true">
  <Draw d="M15 25 C 5 18, 2 12, 4 7 C 6 2, 13 2, 15 8 C 17 2, 24 2, 26 7 C 28 12, 25 18, 15 25 Z" delay={delay} width={2.2} />
</svg>;
export const VIEW = { once: true, margin: "-80px" } as const;
export const DENVER = "America/Denver";
export const datePart = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleDateString("en-US", { timeZone: DENVER, ...o });


/** A pen stroke that draws itself when scrolled into view. */
export function Ink({ d, delay = 0, width = 3 }: { d: string; delay?: number; width?: number }) {
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={VIEW}
    transition={{ pathLength: { duration: 0.6, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

export const InkHeart = ({ delay = 0 }: { delay?: number }) => <svg className="p2-heart" viewBox="0 0 30 28" aria-hidden="true">
  <Ink d="M15 25 C 5 18, 2 12, 4 7 C 6 2, 13 2, 15 8 C 17 2, 24 2, 26 7 C 28 12, 25 18, 15 25 Z" delay={delay} width={2.2} />
</svg>;

/** A word with the hero's yellow marker underline. */
export function U({ children }: { children: ReactNode }) {
  return <span className="p2-u">{children}<svg viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true"><Ink d="M4 12 C 80 6, 200 6, 296 9" delay={0.5} width={7} /></svg></span>;
}

export function Head({ n, kicker, id, children, note, className = "" }: { n: string; kicker: string; id: string; children: ReactNode; note?: ReactNode; className?: string }) {
  return <motion.header className={`p2-head ${className}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
    <p className="p2-kicker"><span>{n}</span>{kicker}</p>
    <h2 id={id}>{children}</h2>
    {note && <p className="p2-pen p2-head-note">{note}</p>}
  </motion.header>;
}

export function useWidth<T extends Element>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(1600);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(100, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** A torn edge for a full-width band, drawn in real pixels so the tears keep their size on any screen. */
export function TornEdge({ fill, seed, side }: { fill: string; seed: number; side: "top" | "bottom" }) {
  const [ref, w] = useWidth<SVGSVGElement>();
  const H = 30, r = rng(seed), line: [number, number][] = [];
  for (let x = 0; x < w; x += 140) line.push([x, 9 + r() * 12]);
  line.push([w, 9 + r() * 12]);
  // The fiber layer sits on the left of the drawing direction, so draw toward the paper's outside.
  const edge = side === "top" ? [...line].reverse() : line;
  const close: [number, number][] = side === "top" ? [[0, H + 2], [w, H + 2]] : [[w, -2], [0, -2]];
  return <svg ref={ref} className={`p2-edge p2-edge-${side}`} viewBox={`0 0 ${w} ${H}`} preserveAspectRatio="none" aria-hidden="true">
    <Torn seed={seed} fill={fill} edge={edge} close={close} fiber={4} jag={0.55} />
  </svg>;
}

export function Band({ color, seed, className, children, top = true, bottom = true, id, label }: { color: string; seed: number; className: string; children: ReactNode; top?: boolean; bottom?: boolean; id?: string; label: string }) {
  return <section id={id} className={`p2-band ${className}`} style={{ background: color }} aria-labelledby={label}>
    {top && <TornEdge fill={color} seed={seed} side="top" />}
    {children}
    {bottom && <TornEdge fill={color} seed={seed + 50} side="bottom" />}
  </section>;
}
